import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CardActionArea from '@mui/material/CardActionArea';
import CircularProgress from '@mui/material/CircularProgress';
import BookIcon from '@mui/icons-material/Book';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { useReadableNovels } from '../hooks/use-readable-novels';
import { ReaderSidebar } from './ReaderSidebar';
import { SecureImage } from '@/components/secure-image';

interface ReadingProgress {
  chapterId: string;
  percent: number;
  updatedAt: string;
}

export function LibraryView() {
  const { t } = useTranslation();
  const { readableNovels, isLoading } = useReadableNovels();
  const [progressMap, setProgressMap] = useState<Record<string, ReadingProgress>>({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem('eplotera_reading_progress');
      if (stored) {
        setProgressMap(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load reading progress', e);
    }
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <ReaderSidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <ReaderSidebar />
      <Box sx={{ flexGrow: 1, p: 4, overflowY: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
            {t('reader.library.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('reader.library.subtitle')}
          </Typography>
        </Box>

        {readableNovels.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="body1" color="text.secondary">
              {t('reader.library.empty')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {readableNovels.map((novel) => {
              const coverImage = novel.coverImage;
              const coverImageUrl = coverImage?.original.url || novel.coverImageUrl;
              const coverImagePath = coverImage?.original.path || coverImage?.profile.path;
              const progress = progressMap[novel.id]?.percent || 0;

              return (
                <Grid key={novel.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: (theme) => theme.shadows[4],
                      },
                    }}
                  >
                    <Link
                      to="/read/$novelId"
                      params={{ novelId: novel.id }}
                      style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column' }}
                    >
                      <CardActionArea
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        {/* Cover Image Container */}
                        <Box sx={{ position: 'relative', width: '100%', pt: '150%', bgcolor: 'action.hover' }}>
                          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                            {coverImageUrl ? (
                              <SecureImage
                                src={coverImageUrl}
                                path={coverImagePath}
                                alt={novel.title}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  p: 3,
                                  textAlign: 'center',
                                  background: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
                                      : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                }}
                              >
                                <BookIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {novel.title}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Link>
                    
                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {novel.title}
                      </Typography>
                      
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '3.6em',
                        }}
                      >
                        {novel.description || t('reader.library.no_description')}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {progress > 0 ? `${progress}% read` : 'Not started'}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: 'action.hover' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
